import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SupportRequestForm = () => {
  const { isDarkMode } = useTheme();
  const { user, token } = useAuth();

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('normal');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [sendingReply, setSendingReply] = useState({});

  const maxFileSizeMb = 10;
  const maxFileSizeBytes = maxFileSizeMb * 1024 * 1024;
  const maxFiles = 10;

  const resetForm = () => {
    setSubject('');
    setCategory('');
    setPriority('normal');
    setDescription('');
    setAttachments([]);
    setTicketId('');
    setErrorMessage('');
    setSuccessMessage('');
  };

  // Fetch user tickets on component mount
  useEffect(() => {
    if (token) {
      fetchUserTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUserTickets = async () => {
    setLoadingTickets(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/support/tickets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleSendReply = async (ticketId) => {
    const reply = replyText[ticketId]?.trim();
    if (!reply) return;

    setSendingReply({ ...sendingReply, [ticketId]: true });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/support/tickets/${ticketId}/comments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ comment: reply }),
        }
      );

      if (response.ok) {
        // Clear reply text
        setReplyText({ ...replyText, [ticketId]: '' });
        // Refresh tickets to show new comment
        fetchUserTickets();
        setSuccessMessage('Reply sent successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const data = await response.json();
        setErrorMessage(data.error || 'Failed to send reply');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      setErrorMessage('Failed to send reply. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setSendingReply({ ...sendingReply, [ticketId]: false });
    }
  };

  const toggleTicketExpansion = (ticketId) => {
    setExpandedTicketId(expandedTicketId === ticketId ? null : ticketId);
  };

  const handleFilesAdded = useCallback(
    (fileList) => {
      const newFiles = Array.from(fileList || []);
      if (!newFiles.length) return;

      const combined = [...attachments];
      const errors = [];

      // Allowed file types: .log, .txt, .jpg, .jpeg, .png, .pdf
      const allowedTypes = [
        'image/png', 
        'image/jpeg', 
        'image/jpg', 
        'application/pdf',
        'text/plain',
        'text/x-log',
        'application/x-log'
      ];

      newFiles.forEach((file) => {
        const fileExt = file.name.split('.').pop().toLowerCase();
        const isAllowedType = allowedTypes.includes(file.type) || 
                             ['log', 'txt', 'jpg', 'jpeg', 'png', 'pdf'].includes(fileExt);
        
        if (!isAllowedType) {
          errors.push(`Unsupported file type: ${file.name}. Allowed: .log, .txt, .jpg, .jpeg, .png, .pdf`);
          return;
        }
        if (file.size > maxFileSizeBytes) {
          errors.push(
            `${file.name} is too large (max ${maxFileSizeMb} MB per file)`
          );
          return;
        }
        if (combined.length >= maxFiles) {
          errors.push(`Maximum of ${maxFiles} attachments reached`);
          return;
        }
        combined.push(file);
      });

      if (errors.length) {
        setErrorMessage(errors.join(' '));
      } else {
        setErrorMessage('');
      }

      setAttachments(combined);
    },
    [attachments, maxFileSizeBytes, maxFileSizeMb, maxFiles]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFilesAdded(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e) => {
    handleFilesAdded(e.target.files);
    // reset input so same file can be re-selected if removed
    e.target.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setTicketId('');

    if (!subject.trim() || !category || !description.trim()) {
      setErrorMessage('Subject, Category, and Description are required.');
      return;
    }

    if (!token) {
      setErrorMessage('You must be logged in to submit a support request.');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('subject', subject.trim());
      formData.append('category', category);
      formData.append('priority', priority || 'normal');
      formData.append('description', description.trim());
      if (user?.name) formData.append('name', user.name);
      if (user?.email) formData.append('email', user.email);

      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/support/tickets`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg =
          data.error ||
          data.message ||
          'We couldn’t submit your request. Please check your connection and try again.';
        throw new Error(msg);
      }

      setTicketId(data.ticket_id || '');
      setSuccessMessage(
        data.message ||
          'Your support request has been submitted. We have sent it to the ZapRF support team.'
      );
      // Refresh ticket list
      fetchUserTickets();
      // keep the form values visible, but you can reset if desired:
      // resetForm();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Support request error:', err);
      setErrorMessage(
        err?.message ||
          'We couldn’t submit your request. Please check your connection and try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const baseCardClasses = `p-6 rounded-xl border shadow-sm ${
    isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const labelClasses = `block text-sm font-medium mb-1 ${
    isDarkMode ? 'text-gray-200' : 'text-gray-700'
  }`;

  const inputClasses = `mt-1 block w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-purple ${
    isDarkMode
      ? 'bg-gray-900/60 border-gray-700 text-gray-100 placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  const selectClasses = inputClasses;
  const textareaClasses = `${inputClasses} min-h-[120px] resize-vertical`;

  return (
    <div className="space-y-6 animate-fadeIn">
      <section className={baseCardClasses}>
        <div className="mb-4">
          <h3
            className={`text-xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}
          >
            Submit a Support Request
          </h3>
          <p
            className={`mt-1 text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            Describe the issue you’re facing with ZapRF. This request will be
            sent to the ZapRF support team at support@zaprf.com, along with any
            screenshots or documents you attach.
          </p>
        </div>

        {(successMessage || errorMessage) && (
          <div className="mb-4 space-y-2">
            {successMessage && (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  isDarkMode
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                    : 'border-emerald-500/20 bg-emerald-50 text-emerald-800'
                }`}
              >
                <p className="font-medium">
                  {ticketId
                    ? `Your support request has been submitted. Ticket ID: #${ticketId}.`
                    : 'Your support request has been submitted.'}
                </p>
                <p className="mt-1">
                  We’ve sent your request to the ZapRF team at support@zaprf.com
                  and, if available, to your email address.
                </p>
              </div>
            )}
            {errorMessage && (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  isDarkMode
                    ? 'border-red-500/40 bg-red-500/10 text-red-200'
                    : 'border-red-500/20 bg-red-50 text-red-800'
                }`}
              >
                {errorMessage}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* User context */}
          {user && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>Name</label>
                <input
                  type="text"
                  value={user.name || ''}
                  readOnly
                  className={`${inputClasses} opacity-80 cursor-not-allowed`}
                />
              </div>
              <div>
                <label className={labelClasses}>Email</label>
                <input
                  type="email"
                  value={user.email || ''}
                  readOnly
                  className={`${inputClasses} opacity-80 cursor-not-allowed`}
                />
              </div>
            </div>
          )}

          {/* Subject */}
          <div>
            <label className={labelClasses}>
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of the issue"
              className={inputClasses}
              required
            />
          </div>

          {/* Category & Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={selectClasses}
                required
              >
                <option value="">Select a category</option>
                <option value="installation">Installation</option>
                <option value="license_billing">License / Billing</option>
                <option value="software_bug">Software Bug</option>
                <option value="feature_request">Feature Request</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className={labelClasses}>Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className={selectClasses}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High (blocking your use of ZapRF)</option>
              </select>
              <p
                className={`mt-1 text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                High = blocking your ability to use ZapRF or causing major
                disruption.
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={labelClasses}>
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={textareaClasses}
              placeholder="Include steps to reproduce, what you expected vs. what happened, any error messages, and relevant timestamps or project details."
              required
            />
          </div>

          {/* Attachments */}
          <div>
            <label className={labelClasses}>Attachments</label>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${
                isDarkMode
                  ? 'border-gray-600 hover:border-brand-purple/70 bg-gray-900/40'
                  : 'border-gray-300 hover:border-brand-purple/70 bg-gray-50'
              }`}
            >
              <input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.pdf,.log,.txt,image/png,image/jpeg,application/pdf,text/plain,text/x-log"
                className="hidden"
                id="support-attachments-input"
                onChange={handleFileInputChange}
              />
              <label
                htmlFor="support-attachments-input"
                className="flex flex-col items-center space-y-1 cursor-pointer"
              >
                <span
                  className={`text-sm font-medium ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-800'
                  }`}
                >
                  + Add attachments
                </span>
                <span
                  className={`text-xs ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Drag and drop or click to upload. Accepted: .log, .txt, .jpg, .jpeg, .png, .pdf. Up to{' '}
                  {maxFiles} files, max {maxFileSizeMb} MB each.
                </span>
              </label>
            </div>

            {attachments.length > 0 && (
              <ul className="mt-3 space-y-2 text-sm">
                {attachments.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className={`flex items-center justify-between rounded-md px-3 py-2 ${
                      isDarkMode ? 'bg-gray-900/60' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col">
                      <span
                        className={
                          isDarkMode ? 'text-gray-100' : 'text-gray-800'
                        }
                      >
                        {file.name}
                      </span>
                      <span
                        className={`text-xs ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}
                      >
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(index)}
                      className="text-xs font-medium text-red-500 hover:text-red-600"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isSubmitting
                  ? 'opacity-80 cursor-not-allowed'
                  : 'cursor-pointer'
              } ${
                isDarkMode
                  ? 'bg-brand-purple text-white hover:bg-brand-purple-dark focus:ring-brand-purple'
                  : 'bg-brand-purple text-white hover:bg-brand-purple-dark focus:ring-brand-purple'
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              disabled={isSubmitting}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium border ${
                isDarkMode
                  ? 'border-gray-600 text-gray-200 hover:bg-gray-800'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              } ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              Clear
            </button>
          </div>
        </form>
      </section>

      {/* Ticket History Section */}
      <section className={baseCardClasses}>
        <div className="mb-4">
          <h3
            className={`text-xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}
          >
            Your Support Tickets
          </h3>
          <p
            className={`mt-1 text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            Track the status of your submitted support tickets
          </p>
        </div>

        {loadingTickets ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-purple"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div
            className={`text-center py-8 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            <p>No support tickets yet. Submit your first ticket above!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => {
              const statusColors = {
                open: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
                in_review: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
                resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
                closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
              };

              const priorityColors = {
                low: 'text-gray-600 dark:text-gray-400',
                normal: 'text-blue-600 dark:text-blue-400',
                high: 'text-red-600 dark:text-red-400'
              };

              const isExpanded = expandedTicketId === ticket.ticket_id;

              return (
                <div
                  key={ticket.id}
                  className={`border rounded-lg p-4 ${
                    isDarkMode
                      ? 'border-gray-700 bg-gray-900/40'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4
                          className={`font-semibold ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {ticket.subject}
                        </h4>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            statusColors[ticket.status]
                          }`}
                        >
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                        <div>
                          <span
                            className={`${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Ticket ID:
                          </span>{' '}
                          <span
                            className={`font-mono font-medium ${
                              isDarkMode ? 'text-gray-200' : 'text-gray-700'
                            }`}
                          >
                            #{ticket.ticket_id}
                          </span>
                        </div>
                        <div>
                          <span
                            className={`${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Priority:
                          </span>{' '}
                          <span
                            className={`font-medium ${
                              priorityColors[ticket.priority]
                            }`}
                          >
                            {ticket.priority.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <span
                            className={`${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Category:
                          </span>{' '}
                          <span
                            className={`${
                              isDarkMode ? 'text-gray-200' : 'text-gray-700'
                            }`}
                          >
                            {ticket.category.replace('_', ' ')}
                          </span>
                        </div>
                        <div>
                          <span
                            className={`${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Created:
                          </span>{' '}
                          <span
                            className={`${
                              isDarkMode ? 'text-gray-200' : 'text-gray-700'
                            }`}
                          >
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <p
                        className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        } ${isExpanded ? '' : 'line-clamp-2'}`}
                      >
                        {ticket.description}
                      </p>
                      
                      {/* Comments count */}
                      {ticket.comments && ticket.comments.length > 0 && (
                        <div
                          className={`mt-2 text-xs ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          💬 {ticket.comments.length} comment
                          {ticket.comments.length !== 1 ? 's' : ''}
                        </div>
                      )}
                      
                      {/* Attachments count */}
                      {ticket.attachments && ticket.attachments.length > 0 && (
                        <div
                          className={`mt-1 text-xs ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          📎 {ticket.attachments.length} attachment
                          {ticket.attachments.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    
                    {/* Expand/Collapse Button */}
                    <button
                      onClick={() => toggleTicketExpansion(ticket.ticket_id)}
                      className={`ml-4 px-3 py-1 text-xs font-medium rounded transition ${
                        isDarkMode
                          ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {isExpanded ? '▲ Collapse' : '▼ Details'}
                    </button>
                  </div>
                  
                  {/* Expanded View - All Comments and Reply */}
                  {isExpanded && (
                    <div
                      className={`mt-4 pt-4 border-t ${
                        isDarkMode ? 'border-gray-700' : 'border-gray-200'
                      }`}
                    >
                      {/* All Comments */}
                      {ticket.comments && ticket.comments.length > 0 ? (
                        <div className="space-y-3 mb-4">
                          <h5
                            className={`text-sm font-semibold ${
                              isDarkMode ? 'text-gray-200' : 'text-gray-700'
                            }`}
                          >
                            Conversation:
                          </h5>
                          {ticket.comments.map((comment, idx) => (
                            <div
                              key={idx}
                              className={`p-3 rounded-lg ${
                                comment.is_admin
                                  ? isDarkMode
                                    ? 'bg-blue-900/20 border border-blue-700/30'
                                    : 'bg-blue-50 border border-blue-200'
                                  : isDarkMode
                                  ? 'bg-gray-800/50 border border-gray-700'
                                  : 'bg-gray-100 border border-gray-200'
                              }`}
                            >
                              <div className="flex items-start gap-2 mb-1">
                                <span
                                  className={`text-xs font-semibold ${
                                    comment.is_admin
                                      ? 'text-blue-600 dark:text-blue-400'
                                      : 'text-gray-600 dark:text-gray-400'
                                  }`}
                                >
                                  {comment.is_admin ? '🛠️ Support Team' : '👤 You'}
                                </span>
                                <span
                                  className={`text-xs ${
                                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                  }`}
                                >
                                  {new Date(comment.created_at).toLocaleString()}
                                </span>
                              </div>
                              <p
                                className={`text-sm whitespace-pre-wrap ${
                                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                                }`}
                              >
                                {comment.comment}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p
                          className={`text-sm mb-4 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          No comments yet. Be the first to reply!
                        </p>
                      )}
                      
                      {/* Reply Input - Only show if ticket is not closed */}
                      {ticket.status !== 'closed' && (
                        <div className="mt-4">
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              isDarkMode ? 'text-gray-200' : 'text-gray-700'
                            }`}
                          >
                            Add a Reply:
                          </label>
                          <textarea
                            value={replyText[ticket.ticket_id] || ''}
                            onChange={(e) =>
                              setReplyText({ ...replyText, [ticket.ticket_id]: e.target.value })
                            }
                            placeholder="Type your reply here..."
                            className={`w-full px-3 py-2 rounded-lg border text-sm ${
                              isDarkMode
                                ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500'
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                            }`}
                            rows="3"
                          />
                          <button
                            onClick={() => handleSendReply(ticket.ticket_id)}
                            disabled={
                              !replyText[ticket.ticket_id]?.trim() || sendingReply[ticket.ticket_id]
                            }
                            className={`mt-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                              !replyText[ticket.ticket_id]?.trim() || sendingReply[ticket.ticket_id]
                                ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                                : isDarkMode
                                ? 'bg-brand-purple text-white hover:bg-brand-purple-dark'
                                : 'bg-brand-purple text-white hover:bg-brand-purple-dark'
                            }`}
                          >
                            {sendingReply[ticket.ticket_id] ? 'Sending...' : 'Send Reply'}
                          </button>
                        </div>
                      )}
                      
                      {ticket.status === 'closed' && (
                        <div
                          className={`text-sm italic ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          This ticket is closed. Comments are disabled.
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Latest comment preview (only when collapsed) */}
                  {!isExpanded && ticket.comments && ticket.comments.length > 0 && (
                    <div
                      className={`mt-3 pt-3 border-t text-sm ${
                        isDarkMode ? 'border-gray-700' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className={`text-xs font-medium ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          {ticket.comments[ticket.comments.length - 1].is_admin
                            ? '🛠️ Support Team:'
                            : '👤 You:'}
                        </span>
                        <p
                          className={`text-xs flex-1 ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-600'
                          } line-clamp-2`}
                        >
                          {ticket.comments[ticket.comments.length - 1].comment}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default SupportRequestForm;


